import {
  useState, useRef, useCallback 
} from 'react';
import { useReactToPrint } from 'react-to-print';
import {
  ListChecks, Package, Clock, CheckCircle2, Printer
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  PickListCard, PrintablePickList
} from '@/features/requisitions';
import type { Requisition } from '@/features/requisitions';
import { toast } from 'sonner';

interface PickListsViewProps {
  readonly requisitions: Requisition[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly onRefetch: () => void;
  readonly onFulfill: (requisitionId: string) => void;
}

export function PickListsView({
  requisitions,
  isLoading,
  error,
  onRefetch,
  onFulfill,
}: PickListsViewProps) {
  const [pickedItemsMap, setPickedItemsMap] = useState<Record<string, Set<string>>>({});
  const [printingRequisition, setPrintingRequisition] = useState<Requisition | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: printingRequisition
      ? `PickList-${printingRequisition.requisitionId}`
      : 'PickList',
    onAfterPrint: () => setPrintingRequisition(null),
  });

  const handlePrintClick = useCallback((requisition: Requisition) => {
    setPrintingRequisition(requisition);
    setTimeout(() => {
      handlePrint();
    }, 100);
  }, [handlePrint]);

  const toggleItem = useCallback((requisitionId: string, lineId: string) => {
    setPickedItemsMap((prev) => {
      const current = prev[requisitionId] ?? new Set<string>();
      const updated = new Set(current);
      if (updated.has(lineId)) {
        updated.delete(lineId);
      } else {
        updated.add(lineId);
      }
      return {
        ...prev,
        [requisitionId]: updated 
      };
    });
  }, []);

  const handleMarkComplete = useCallback((requisition: Requisition) => {
    onFulfill(requisition.requisitionId);
    toast.success(`Requisition ${requisition.requisitionId} marked as fulfilled`);
  }, [onFulfill]);

  // Stats
  const totalItems = requisitions.reduce((sum, r) => sum + r.totalItems, 0);
  const urgentCount = requisitions.filter((r) => {
    const neededBy = new Date(r.neededBy);
    return neededBy <= new Date(Date.now() + 24 * 60 * 60 * 1000);
  }).length;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={`stat-skel-${i.toString()}`} className="h-24 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
        <div className="space-y-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={`list-skel-${i.toString()}`} className="h-64 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <ListChecks className="mb-4 h-12 w-12 text-red-500" />
          <p className="text-lg font-medium">Failed to load pick lists</p>
          <p className="text-sm text-muted-foreground">{error.message}</p>
          <Button variant="outline" className="mt-4" onClick={onRefetch}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (requisitions.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <CheckCircle2 className="mb-4 h-12 w-12 text-green-500" />
          <p className="text-lg font-medium">All caught up</p>
          <p className="text-sm text-muted-foreground">
            No approved requisitions waiting to be picked
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pick Lists
            </CardTitle>
            <ListChecks className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{requisitions.length}</div>
            <p className="text-xs text-muted-foreground">Ready to pick</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Items
            </CardTitle>
            <Package className="h-5 w-5 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalItems}</div>
            <p className="text-xs text-muted-foreground">Across all lists</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Urgent
            </CardTitle>
            <Clock className="h-5 w-5 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{urgentCount}</div>
            <p className="text-xs text-muted-foreground">Due within 24 hours</p>
          </CardContent>
        </Card>
      </div>

      {/* Print All Button */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={() => {
            toast.info('Print all functionality coming soon');
          }}
        >
          <Printer className="mr-2 h-4 w-4" />
          Print All Lists
        </Button>
      </div>

      {/* Pick Lists */}
      <div className="space-y-4">
        {requisitions.map((requisition) => (
          <PickListCard
            key={requisition.requisitionId}
            requisition={requisition}
            pickedItems={pickedItemsMap[requisition.requisitionId] ?? new Set()}
            onToggleItem={(lineId) => toggleItem(requisition.requisitionId, lineId)}
            onPrint={() => handlePrintClick(requisition)}
            onMarkComplete={() => handleMarkComplete(requisition)}
          />
        ))}
      </div>

      {/* Hidden printable component */}
      <div className="hidden">
        {printingRequisition && (
          <PrintablePickList ref={printRef} requisition={printingRequisition} />
        )}
      </div>
    </div>
  );
}
